/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { type ContextValue, Provider, ToggleButton as ToggleButtonCore, type ToggleButtonProps as ToggleButtonCoreProps, useContextProps } from 'react-aria-components';
import { IconContext } from '../icon/icon.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import type { TextProps } from '../text/text.component';
import { Text, TextContext } from '../text/text.component';

interface SegmentedButtonProps extends Omit<ToggleButtonCoreProps, 'style' | 'className'> {
  size?: keyof typeof sizeVariants;
  style?: stylex.StyleXStyles;
}

const SegmentedButtonContext = createContext<ContextValue<SegmentedButtonProps, HTMLButtonElement>>({});

const SegmentedButton = forwardRef((props: SegmentedButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, SegmentedButtonContext);

  const { style, children, size = 32, ...coreProps } = props;

  return (
    <ToggleButtonCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], style)}
      {...coreProps}
      data-id={coreProps.id}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(evaluatedChildren).every(c => !isValidElement(c));

        return (
          <Provider
            values={[
              [TextContext, {
                size: TEXT_SIZES[size],
                color: (
                  (renderProps.isDisabled && 'disabled') ||
                  (renderProps.isSelected && 'primary') ||
                  'tertiary'
                )
              }],
              [IconContext, {
                size: size === 40 ? 18 : 16,
                color: (
                  (renderProps.isDisabled && 'disabled') ||
                  (renderProps.isSelected && 'primary') ||
                  'tertiary'
                )
              }],
              [NumberBadgeContext, {
                size: 16,
                color: 'neutral',
                isPastel: !renderProps.isSelected,
              }]
            ]}>
            {typeof evaluatedChildren === 'string' || isTextOnly ? <Text>{evaluatedChildren}</Text> : evaluatedChildren}
          </Provider>
        );
      }}
    </ToggleButtonCore>
  );
});

const TEXT_SIZES: Record<keyof typeof sizeVariants, TextProps['size']> = {
  28: 13,
  32: 13,
  40: 14,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderStyle: 'none',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
    gap: 6,
    height: 24,
    justifyContent: 'center',
    minWidth: 40,
    zIndex: 2,
  },
});

const sizeVariants = stylex.create({
  28: {
    height: 28,
    paddingInline: 12,
  },
  32: {
    height: 32,
    paddingInline: 12,
  },
  40: {
    height: 40,
    paddingInline: 16,
  },
});

export { SegmentedButton, SegmentedButtonContext, type SegmentedButtonProps };
